import React, { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  List,
  ListItemButton,
  Typography,
  TextField,
  Alert,
  Snackbar,
  useTheme,
  InputAdornment,
  alpha,
  Avatar,
  IconButton,
  Tooltip,
} from "@mui/material";
import TagIcon from "@mui/icons-material/Tag";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import SupportAgentOutlinedIcon from "@mui/icons-material/SupportAgentOutlined";
import { useTranslation } from "react-i18next";
import CreateChannelDialog from "../components/CreateChannelDialog";
import StartConversationDialog from "../components/StartConversationDialog";
import SidebarSection from "../../../components/TeamChat/SidebarSection";
import { useEffectivePermissions, scopeSetFromPermissions } from "../../../hooks/useEffectivePermissions";
import { useAuthContext } from "../../../context/AuthContext";
import { getApiErrorMessage } from "../../../utils/apiErrorMessage";
import { getTeamSpaceLabel } from "../../../components/TeamChat/teamSpaceLabel";
import { getInitials } from "../../../utils/utils";
import TeamPresenceAvatar from "../../../components/TeamPresenceAvatar";
import TeamPresenceRoster from "../../../components/TeamPresenceRoster";
import { findUserDmSpace } from "../utils/teamUiUtils";
import TeamSpaceListSubtitle from "../components/TeamSpaceListSubtitle";
import { openTeamPopupWindow } from "../../../utils/openTeamPopupWindow";
import { isPopupTeamContext } from "../../../utils/teamNavigation";
import { useActiveVideoRooms } from "../../../hooks/useActiveVideoRooms";
import { ActiveVideoIndicator } from "../../../components/ActiveVideo/ActiveVideoIndicator";
import { isEntityDiscussionSpace } from "../utils/teamMentions";
import { partitionTeamSidebarSpaces, sumSpaceUnreadCount } from "../utils/teamSpaceGroups";

const COLLAPSE_STORAGE_KEY = "xproTeamSidebarCollapsed";

function loadCollapsedState() {
  try {
    const raw = localStorage.getItem(COLLAPSE_STORAGE_KEY);
    if (!raw) return { channels: false, requestChannels: false, dms: false };
    const parsed = JSON.parse(raw);
    return {
      channels: Boolean(parsed.channels),
      requestChannels: Boolean(parsed.requestChannels),
      dms: Boolean(parsed.dms),
    };
  } catch {
    return { channels: false, requestChannels: false, dms: false };
  }
}

const SpaceListPane = ({
  containerStyle,
  isMobile,
  spaces,
  selectedSpaceId,
  onSelectSpace,
  onCreateChannel,
  onCreateDm,
  isCreating,
  currentUserId,
  basePath = "/equipe",
  showOpenPopupButton = false,
  onRefresh,
}) => {
  const { t } = useTranslation("team");
  const navigate = useNavigate();
  const theme = useTheme();
  const showPopupActions = !isPopupTeamContext(basePath);
  const [channelOpen, setChannelOpen] = useState(false);
  const [conversationOpen, setConversationOpen] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [search, setSearch] = useState("");
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [collapsed, setCollapsed] = useState(loadCollapsedState);
  const { userData } = useAuthContext();
  const isMasterUser = Number(userData?.master) === 1;
  const { data: permissionsData } = useEffectivePermissions();
  const { byTeamSpaceId } = useActiveVideoRooms();
  const scopes = scopeSetFromPermissions(permissionsData, userData);
  const canCreateChannel = scopes.has("team_chat.view");

  const toggleSection = useCallback((key) => {
    setCollapsed((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      try {
        localStorage.setItem(COLLAPSE_STORAGE_KEY, JSON.stringify(next));
      } catch {
        /* ignore */
      }
      return next;
    });
  }, []);

  const matchesSearch = (space) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    const label = getTeamSpaceLabel(space).toLowerCase();
    const preview = String(space.lastMessagePreview || "").toLowerCase();
    return label.includes(q) || preview.includes(q);
  };

  const { channels, requestChannels, dms } = useMemo(
    () => partitionTeamSidebarSpaces(spaces, matchesSearch),
    [spaces, search]
  );
  const channelsUnread = useMemo(() => sumSpaceUnreadCount(channels), [channels]);
  const requestChannelsUnread = useMemo(() => sumSpaceUnreadCount(requestChannels), [requestChannels]);
  const dmsUnread = useMemo(() => sumSpaceUnreadCount(dms), [dms]);

  const handleRefresh = useCallback(async () => {
    if (typeof onRefresh !== "function" || isRefreshing) return;
    setIsRefreshing(true);
    try {
      await onRefresh();
    } catch {
      /* keep current list; user can retry */
    } finally {
      setIsRefreshing(false);
    }
  }, [onRefresh, isRefreshing]);

  const handleStartConversation = async (payload) => {
    if (!payload) return;
    try {
      const res = await onCreateDm(payload);
      if (res?.id) onSelectSpace(res.id);
    } catch (err) {
      setErrorMsg(getApiErrorMessage(err, t("dmOpenError")));
      throw err;
    }
  };

  const handleOpenUserDm = useCallback(
    async (user) => {
      const uid = Number(user?.id);
      if (!Number.isFinite(uid) || uid <= 0) return;
      const existing = findUserDmSpace(spaces, uid);
      if (existing) {
        onSelectSpace(existing.id);
        return;
      }
      if (isMasterUser) {
        setErrorMsg(t("masterDmCannotOpen"));
        return;
      }
      try {
        const res = await onCreateDm({ userId: uid });
        if (res?.id) onSelectSpace(res.id);
      } catch (err) {
        setErrorMsg(getApiErrorMessage(err, t("dmOpenError")));
        throw err;
      }
    },
    [spaces, onSelectSpace, onCreateDm, t, isMasterUser]
  );

  const renderChannelItem = (space) => {
    const selected = Number(selectedSpaceId) === Number(space.id);
    const unread = Number(space.unreadCount) || 0;
    const label = getTeamSpaceLabel(space);
    const showHash = space.type === "channel" && !String(label).startsWith("#");

    return (
      <ListItemButton
        key={space.id}
        selected={selected}
        onClick={() => onSelectSpace(space.id)}
        sx={{
          mx: 1,
          mb: 0.25,
          borderRadius: 1.5,
          py: 1,
          color: "text.primary",
          "&.Mui-selected": {
            bgcolor: alpha(theme.palette.primary.main, 0.1),
            "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.14) },
          },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.25, width: "100%", minWidth: 0 }}>
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              color: selected ? "primary.main" : "text.secondary",
              bgcolor: alpha(theme.palette.primary.main, selected ? 0.12 : 0.06),
            }}
          >
            {isEntityDiscussionSpace(space) ? (
              <AssignmentOutlinedIcon sx={{ fontSize: 16 }} />
            ) : space.isPrivate ? (
              <LockOutlinedIcon sx={{ fontSize: 16 }} />
            ) : (
              <TagIcon sx={{ fontSize: 16 }} />
            )}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Typography variant="body2" color="text.primary" fontWeight={unread > 0 ? 700 : 500} noWrap sx={{ flex: 1 }}>
                {showHash ? `#${label}` : label}
              </Typography>
              <ActiveVideoIndicator room={byTeamSpaceId.get(Number(space.id))} />
              {unread > 0 ? (
                <Box
                  sx={{
                    minWidth: 20,
                    height: 20,
                    px: 0.75,
                    borderRadius: 10,
                    bgcolor: "primary.main",
                    color: "primary.contrastText",
                    fontSize: "0.7rem",
                    fontWeight: 700,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {unread > 9 ? "9+" : unread}
                </Box>
              ) : null}
            </Box>
            <TeamSpaceListSubtitle
              spaceId={space.id}
              lastMessagePreview={space.lastMessagePreview}
            />
          </Box>
        </Box>
      </ListItemButton>
    );
  };

  const renderDmAvatar = (space, selected, label) => {
    if (space.dmPeerType === "agent") {
      return (
        <Avatar
          sx={{
            width: 32,
            height: 32,
            bgcolor: selected ? "secondary.main" : alpha(theme.palette.secondary.main, 0.15),
            color: selected ? "secondary.contrastText" : "secondary.main",
          }}
        >
          <SmartToyOutlinedIcon sx={{ fontSize: 18 }} />
        </Avatar>
      );
    }
    if (space.dmPeerType === "chatbot") {
      return (
        <Avatar
          sx={{
            width: 32,
            height: 32,
            bgcolor: selected ? "info.main" : alpha(theme.palette.info.main, 0.15),
            color: selected ? "info.contrastText" : "info.main",
          }}
        >
          <SupportAgentOutlinedIcon sx={{ fontSize: 18 }} />
        </Avatar>
      );
    }
    const peerId = space.dmPeerUserId;
    if (peerId) {
      return (
        <TeamPresenceAvatar
          userId={peerId}
          src={space.dmPeerAvatarUrl}
          name={label}
          size={32}
          sx={{
            "& .MuiAvatar-root": {
              bgcolor: selected ? "primary.main" : alpha(theme.palette.primary.main, 0.15),
              color: selected ? "primary.contrastText" : "primary.main",
            },
          }}
        />
      );
    }
    return (
      <Avatar
        sx={{
          width: 32,
          height: 32,
          fontSize: 13,
          bgcolor: selected ? "primary.main" : alpha(theme.palette.primary.main, 0.15),
          color: selected ? "primary.contrastText" : "primary.main",
        }}
      >
        {getInitials(label)}
      </Avatar>
    );
  };

  const renderDmItem = (space) => {
    const selected = Number(selectedSpaceId) === Number(space.id);
    const unread = Number(space.unreadCount) || 0;
    const label = getTeamSpaceLabel(space);

    return (
      <ListItemButton
        key={space.id}
        selected={selected}
        onClick={() => onSelectSpace(space.id)}
        sx={{
          mx: 1,
          mb: 0.25,
          borderRadius: 1.5,
          py: 1,
          color: "text.primary",
          "&.Mui-selected": {
            bgcolor: alpha(theme.palette.primary.main, 0.1),
            "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.14) },
          },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.25, width: "100%", minWidth: 0 }}>
          {renderDmAvatar(space, selected, label)}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Typography variant="body2" color="text.primary" fontWeight={unread > 0 ? 700 : 500} noWrap sx={{ flex: 1 }}>
                {label}
              </Typography>
              <ActiveVideoIndicator room={byTeamSpaceId.get(Number(space.id))} />
              {unread > 0 ? (
                <Box
                  sx={{
                    minWidth: 20,
                    height: 20,
                    px: 0.75,
                    borderRadius: 10,
                    bgcolor: "primary.main",
                    color: "primary.contrastText",
                    fontSize: "0.7rem",
                    fontWeight: 700,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {unread > 9 ? "9+" : unread}
                </Box>
              ) : null}
            </Box>
            <TeamSpaceListSubtitle
              spaceId={space.id}
              lastMessagePreview={space.lastMessagePreview}
            />
          </Box>
        </Box>
      </ListItemButton>
    );
  };

  return (
    <Box
      sx={{
        width: isMobile ? "100%" : { xs: "100%", md: "25%" },
        minWidth: isMobile ? 0 : 300,
        flex: isMobile ? 1 : undefined,
        bgcolor: theme.palette.background.paper,
        color: theme.palette.text.primary,
        display: "flex",
        flexDirection: "column",
        alignSelf: "stretch",
        flexShrink: 0,
        minHeight: 0,
        ...containerStyle,
      }}
    >
      <Box sx={{ px: 2, pt: isMobile ? 1.25 : 1.75, pb: 1.25, borderBottom: 1, borderColor: "divider", flexShrink: 0 }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5, mb: isMobile ? 1 : 0 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" fontWeight={700} color="text.primary">
              {t("title")}
            </Typography>
            {!isMobile ? (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25, mb: 1.25 }}>
                {t("sidebarSubtitle")}
              </Typography>
            ) : null}
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", flexShrink: 0, mt: -0.25 }}>
            {typeof onRefresh === "function" ? (
              <Tooltip title={t("refreshSpaces")}>
                <span>
                  <IconButton
                    size="small"
                    onClick={handleRefresh}
                    disabled={isRefreshing}
                    aria-label={t("refreshSpaces")}
                  >
                    <RefreshIcon
                      fontSize="small"
                      sx={
                        isRefreshing
                          ? {
                              animation: "teamSpacesRefreshSpin 0.8s linear infinite",
                              "@keyframes teamSpacesRefreshSpin": {
                                to: { transform: "rotate(360deg)" },
                              },
                            }
                          : undefined
                      }
                    />
                  </IconButton>
                </span>
              </Tooltip>
            ) : null}
            {showOpenPopupButton ? (
              <Tooltip title={t("openPopupWindowTooltip")}>
                <IconButton
                  size="small"
                  onClick={() => openTeamPopupWindow(selectedSpaceId)}
                  aria-label={t("openPopupWindow")}
                >
                  <OpenInNewIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : null}
          </Box>
        </Box>

        <TextField
          size="small"
          fullWidth
          placeholder={t("searchSpaces")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
          }}
          sx={{
            "& .MuiOutlinedInput-root": { borderRadius: 2, bgcolor: "action.hover" },
            "& .MuiOutlinedInput-input::placeholder": {
              color: theme.palette.input?.placeholder || theme.palette.text.secondary,
              opacity: 1,
            },
          }}
        />
      </Box>

      <Box sx={{ flex: 1, overflow: "auto", py: 0.5 }} className="sidebar-scroll">
        {!isMasterUser ? (
        <SidebarSection
          title={t("directMessages")}
          count={dms.length}
          unreadCount={dmsUnread}
          collapsed={collapsed.dms}
          onToggleCollapse={() => toggleSection("dms")}
          showAdd
          addLabel={t("newDm")}
          onAdd={() => setConversationOpen(true)}
        >
          <List dense disablePadding>
            {dms.length ? (
              dms.map(renderDmItem)
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 1 }}>
                {search ? t("noSearchResults") : t("noDms")}
              </Typography>
            )}
          </List>
        </SidebarSection>
        ) : null}

        <SidebarSection
          title={t("channels")}
          count={channels.length}
          unreadCount={channelsUnread}
          collapsed={collapsed.channels}
          onToggleCollapse={() => toggleSection("channels")}
          showAdd={canCreateChannel}
          addLabel={t("newChannel")}
          onAdd={() => setChannelOpen(true)}
        >
          <List dense disablePadding>
            {channels.length ? (
              channels.map(renderChannelItem)
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 1 }}>
                {search ? t("noSearchResults") : t("noChannels")}
              </Typography>
            )}
          </List>
        </SidebarSection>

        <SidebarSection
          title={t("requestChannels")}
          count={requestChannels.length}
          unreadCount={requestChannelsUnread}
          collapsed={collapsed.requestChannels}
          onToggleCollapse={() => toggleSection("requestChannels")}
        >
          <List dense disablePadding>
            {requestChannels.length ? (
              requestChannels.map(renderChannelItem)
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 1 }}>
                {search ? t("noSearchResults") : t("noRequestChannels")}
              </Typography>
            )}
          </List>
        </SidebarSection>
      </Box>

      <TeamPresenceRoster
        compact={isMobile}
        currentUserId={currentUserId}
        onSelectUser={isMasterUser ? undefined : handleOpenUserDm}
      />

      <CreateChannelDialog
        open={channelOpen}
        onClose={() => setChannelOpen(false)}
        onCreate={onCreateChannel}
        isCreating={isCreating}
        onError={(err) => setErrorMsg(getApiErrorMessage(err, t("channelCreateError")))}
      />

      <StartConversationDialog
        open={conversationOpen}
        onClose={() => setConversationOpen(false)}
        onSelect={handleStartConversation}
        isBusy={isCreating}
      />

      <Snackbar
        open={Boolean(errorMsg)}
        autoHideDuration={6000}
        onClose={() => setErrorMsg("")}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="error" onClose={() => setErrorMsg("")} sx={{ width: "100%" }}>
          {errorMsg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SpaceListPane;
